/**
 * Multi-Tab Leader-Election via BroadcastChannel API.
 *
 * Problem: Multiple tabs of AI Farm share one IndexedDB. Without coordination,
 * all tabs fire sync on the 'online' event, producing race-conditions and
 * server load. Atomic-claim dequeue prevents double-processing, but does not
 * prevent multiple sync cycles.
 *
 * Solution: Heartbeat-based leader election. One tab is leader, others are
 * followers. Leader runs sync cycles; followers observe. On leader crash,
 * followers detect via missed heartbeat and re-elect.
 *
 * Pattern: Industry-standard (Postgres pgbouncer, Kafka, ZK ensembles use
 * heartbeat with timeout multiple).
 *
 * Channel name: 'aif_queue_leader'
 * Heartbeat interval: 5000ms
 * Timeout (missed heartbeat threshold): 10000ms (2x interval, safe margin)
 *
 * Tab IDs: random UUID per tab instance. NOT user-scoped — different users
 * in different tabs would share a channel name, but BroadcastChannel is
 * origin-scoped + user is irrelevant for leader election (any tab can sync
 * its OWN queue scope; leader role just prevents simultaneous attempts).
 */

const CHANNEL_NAME = 'aif_queue_leader';
const HEARTBEAT_INTERVAL_MS = 5000;
const LEADER_TIMEOUT_MS = 10000;
const ELECTION_DELAY_MS = 100; // brief jitter to reduce simultaneous announcements

// Message types
const MSG_HEARTBEAT = 'heartbeat';
const MSG_ELECTION = 'election';
const MSG_LEADER_ANNOUNCE = 'leader_announce';
const MSG_LEADER_RESIGN = 'leader_resign';

// Module-level state
let tabId = null;
let channel = null;
let isLeader = false;
let currentLeaderId = null;
let lastLeaderHeartbeat = 0;
let heartbeatTimer = null;
let timeoutChecker = null;
let onBecomeLeaderCallback = null;
let onLoseLeaderCallback = null;
let started = false;

function generateTabId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'tab-' + Math.random().toString(36).slice(2) + '-' + Date.now();
}

function send(type, extra = {}) {
  if (!channel) return;
  try {
    channel.postMessage({ type, tabId, timestamp: Date.now(), ...extra });
  } catch (err) {
    console.warn('[queueLeader] postMessage failed:', err);
  }
}

function handleMessage(event) {
  const { type, tabId: senderId, timestamp } = event.data;
  if (senderId === tabId) return; // ignore own messages

  if (type === MSG_HEARTBEAT) {
    // Track latest heartbeat from current leader
    if (senderId === currentLeaderId) {
      lastLeaderHeartbeat = timestamp;
    } else if (!currentLeaderId) {
      // No current leader; this heartbeat is from a tab claiming leadership
      currentLeaderId = senderId;
      lastLeaderHeartbeat = timestamp;
    } else if (senderId < currentLeaderId) {
      // Conflict: another tab also claims leader; lower tabId wins (deterministic)
      currentLeaderId = senderId;
      lastLeaderHeartbeat = timestamp;
      if (isLeader) {
        isLeader = false;
        if (onLoseLeaderCallback) onLoseLeaderCallback();
      }
    }
  } else if (type === MSG_LEADER_ANNOUNCE) {
    // Another tab is announcing itself as leader
    if (!currentLeaderId || senderId < currentLeaderId) {
      currentLeaderId = senderId;
      lastLeaderHeartbeat = timestamp;
      if (isLeader && senderId < tabId) {
        isLeader = false;
        if (onLoseLeaderCallback) onLoseLeaderCallback();
      }
    }
  } else if (type === MSG_LEADER_RESIGN) {
    if (senderId === currentLeaderId) {
      currentLeaderId = null;
      lastLeaderHeartbeat = 0;
      // Trigger immediate election
      setTimeout(attemptElection, ELECTION_DELAY_MS);
    }
  } else if (type === MSG_ELECTION) {
    // Another tab is initiating election; respond if we're leader
    if (isLeader) {
      send(MSG_LEADER_ANNOUNCE);
    }
  }
}

function attemptElection() {
  if (!started) return;
  const now = Date.now();
  const leaderIsStale = !currentLeaderId || (now - lastLeaderHeartbeat) > LEADER_TIMEOUT_MS;
  if (!leaderIsStale) return;

  // Announce candidacy
  send(MSG_ELECTION);

  // Wait a brief jitter to collect any leader-announce replies
  setTimeout(() => {
    const stillStale = !currentLeaderId || (Date.now() - lastLeaderHeartbeat) > LEADER_TIMEOUT_MS;
    if (stillStale) {
      // No leader responded; claim leadership
      isLeader = true;
      currentLeaderId = tabId;
      lastLeaderHeartbeat = Date.now();
      send(MSG_LEADER_ANNOUNCE);
      if (onBecomeLeaderCallback) onBecomeLeaderCallback();
    }
  }, ELECTION_DELAY_MS * 3);
}

function startHeartbeat() {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(() => {
    if (isLeader) {
      send(MSG_HEARTBEAT);
      lastLeaderHeartbeat = Date.now();
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function startTimeoutChecker() {
  if (timeoutChecker) return;
  timeoutChecker = setInterval(() => {
    if (!isLeader) {
      const now = Date.now();
      const leaderIsStale = currentLeaderId && (now - lastLeaderHeartbeat) > LEADER_TIMEOUT_MS;
      if (leaderIsStale) {
        currentLeaderId = null;
        attemptElection();
      } else if (!currentLeaderId) {
        attemptElection();
      }
    }
  }, HEARTBEAT_INTERVAL_MS);
}

/**
 * Start leader election. Idempotent.
 *
 * @param {object} [opts]
 * @param {() => void} [opts.onBecomeLeader] - Called when this tab becomes leader
 * @param {() => void} [opts.onLoseLeader] - Called when this tab loses leadership
 * @returns {string} The tab ID
 */
export function startLeaderElection(opts = {}) {
  if (started) return tabId;
  if (typeof BroadcastChannel === 'undefined') {
    // Environment without BroadcastChannel (some test environments).
    // Single-tab fallback: this tab IS the leader.
    tabId = generateTabId();
    isLeader = true;
    started = true;
    if (opts.onBecomeLeader) {
      setTimeout(opts.onBecomeLeader, 0);
    }
    return tabId;
  }

  tabId = generateTabId();
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.addEventListener('message', handleMessage);
  onBecomeLeaderCallback = opts.onBecomeLeader || null;
  onLoseLeaderCallback = opts.onLoseLeader || null;
  started = true;

  startHeartbeat();
  startTimeoutChecker();
  attemptElection();

  return tabId;
}

/**
 * Stop leader election (e.g., before tab close). Resigns leadership cleanly
 * so followers can re-elect immediately rather than waiting for timeout.
 */
export function stopLeaderElection() {
  if (!started) return;
  if (isLeader) {
    send(MSG_LEADER_RESIGN);
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  if (timeoutChecker) {
    clearInterval(timeoutChecker);
    timeoutChecker = null;
  }
  if (channel) {
    channel.close();
    channel = null;
  }
  isLeader = false;
  currentLeaderId = null;
  started = false;
}

export function getIsLeader() {
  return isLeader;
}

export function getTabId() {
  return tabId;
}

export function getCurrentLeaderId() {
  return currentLeaderId;
}

// Test-only hooks
export const __testHooks = {
  reset: () => {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    if (timeoutChecker) clearInterval(timeoutChecker);
    if (channel) {
      try { channel.close(); } catch { /* best-effort */ }
    }
    tabId = null;
    channel = null;
    isLeader = false;
    currentLeaderId = null;
    lastLeaderHeartbeat = 0;
    heartbeatTimer = null;
    timeoutChecker = null;
    onBecomeLeaderCallback = null;
    onLoseLeaderCallback = null;
    started = false;
  },
  forceLeadership: () => {
    isLeader = true;
    currentLeaderId = tabId;
    lastLeaderHeartbeat = Date.now();
  },
  HEARTBEAT_INTERVAL_MS,
  LEADER_TIMEOUT_MS,
};
